import { useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import { DataSourceItem } from '@lendi-ui/auto-complete';

import { searchOccupations } from '@funnels/api';
import { FIELD_PATHS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { EmploymentEntityIndex, Occupation } from '@funnels/types';
import { shouldSkipValidate } from '@funnels/utils';

import UncontrolledAutoCompleteInputField from '../../Fields/_uncontrolled/AutoCompleteInputField';
import HiddenField from '../../Fields/HiddenField';

interface EmploymentRoleProps extends EmploymentEntityIndex {
  isOptional?: boolean;
  isDisabled?: boolean;
}

export const EmploymentRole = (props: EmploymentRoleProps) => {
  const { employmentIndex, isOptional, isDisabled } = props;
  const [formValues, setFormValue] = useFormValues();
  const role = get(formValues, FIELD_PATHS.EMPLOYMENT_ROLE_ANZSCO_OCCUPATION(employmentIndex), '');
  const [value, setValue] = useState(role);
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const dataSource = useMemo(() => {
    return occupations.map((occupation) => ({
      label: occupation.anzsco.occupation,
      value: occupation.anzsco.code,
    }));
  }, [occupations]);
  const filteredDataSource = useMemo(() => {
    if (value.length < 1) return [];
    return dataSource
      .filter((item) => item.label && item.label.toLowerCase().includes(value.toLowerCase()))
      .slice(0, 50);
  }, [dataSource, value]);

  useEffect(() => {
    searchOccupations().then(setOccupations);
  }, []);

  return (
    <>
      <UncontrolledAutoCompleteInputField
        dataSource={filteredDataSource}
        label="Job title"
        placeholder="Search"
        isDisabled={isDisabled}
        onChange={(value: string) => {
          setValue(value);
          setFormValue(FIELD_PATHS.EMPLOYMENT_ROLE(employmentIndex), value);
        }}
        onReset={() => {
          setValue('');
          setFormValue(FIELD_PATHS.EMPLOYMENT_ROLE(employmentIndex), undefined);
        }}
        handleSelectedItem={(selectedItem: DataSourceItem) => {
          setValue(selectedItem.label);
          const occupation = occupations.find((o) => o.anzsco.code === selectedItem.value);
          setFormValue(FIELD_PATHS.EMPLOYMENT_ROLE(employmentIndex), occupation);
        }}
        value={value}
        isFullWidth
      />
      <HiddenField
        name={FIELD_PATHS.EMPLOYMENT_ROLE(employmentIndex)}
        validate={(value: any) => {
          if (value?.anzsco?.code && !dataSource.length) {
            return;
          }

          const isValid = value?.anzsco?.code && dataSource.find((data) => data.value === value.anzsco.code);
          if (!shouldSkipValidate(value, isOptional) && !isValid) {
            return 'Please select your job title from the list';
          }
        }}
      />
    </>
  );
};
